/**
 * SAP Business One — socios de negocio vía API de integración.
 *
 * GET `integration/sapb1/business-partners?federalTaxId=...` (Bearer vía `axiosInstance`).
 */
import axiosInstance from '../utils/axiosInstance';
import { getWholesaleCountryFromSession } from '../utils/userSession';

/**
 * @typedef {object} SapB1BusinessPartnerDto
 * @property {string} [CardCode]
 * @property {string} [CardName]
 * @property {string} [Address]
 * @property {string} [FederalTaxID]
 * @property {string} [City]
 * @property {string} [County]
 * @property {string} [MailAddress]
 * @property {string} [MailCity]
 * @property {string} [MailCounty]
 * @property {string|null} [EmailAddress]
 * @property {number} [DiscountPercent]
 * @property {number|null} [PriceListNum] - Lista de precios SAP asignada al cliente (`ItemPrices.PriceList` a usar en la validación).
 * @property {Array<{marca: string, porcentaje: number}>} [DiscountBrand] - Descuentos por marca para el socio (usado en Carga Mayorista para resolver el descuento según la marca de cabecera).
 */

/** Perfiles de carga mayorista con moneda PEN (Excel Perú). */
export const BULK_PERU_WHOLESALE_PROFILE_IDS = [
  'oechsle_peru_oc',
  'ripley_peru_international_apparel',
  'falabella_peru_distributions',
  'ix_comercio_peru',
  'multimarca_peru'
];

/**
 * Lista de precios SAP asignada al cliente (`PriceListNum` del socio de negocio).
 * Es la única fuente de la lista usada para validar precios (no depende del canal de venta).
 *
 * @param {SapB1BusinessPartnerDto|null|undefined} partner
 * @returns {number|null} entero > 0, o `null` si no hay socio o no tiene lista asignada (bloquea, sin lista por defecto)
 */
export function resolvePartnerPriceListNum(partner) {
  const raw = partner?.PriceListNum;
  if (raw == null || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Clave estable para comparar `DiscountBrand[].marca` (mayúsculas, espacios → guión).
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
export function normalizeDiscountBrandMarcaKey(raw) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '-');
}

/**
 * @param {string|null|undefined} brand
 * @returns {boolean}
 */
export function isDockersBrandName(brand) {
  const compact = String(brand ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return compact === 'DOCKERS';
}

/**
 * RUC Perú (11 dígitos) tras normalización.
 * @param {string|number|null|undefined} customerRut
 * @returns {boolean}
 */
export function isPeruFederalTaxIdForWholesale(customerRut) {
  return normalizeFederalTaxIdForQuery(customerRut).length === 11;
}

/**
 * Carga mayorista en contexto Perú.
 * Prioridad: email de sesión (`@iacperu.com` / `@iacchile.com`); si no aplica (demo), perfil Excel PEN o RUC 11 dígitos.
 *
 * @param {{ profileId?: string|null, customerRut?: string|null, sessionCountry?: 'PE'|'CL'|null }} [ctx]
 * @returns {boolean}
 */
export function isBulkPeruWholesaleContext(ctx = {}) {
  const sessionCountry = ctx.sessionCountry ?? getWholesaleCountryFromSession();
  if (sessionCountry === 'PE') return true;
  if (sessionCountry === 'CL') return false;

  const pid = String(ctx?.profileId ?? '').trim();
  if (pid && BULK_PERU_WHOLESALE_PROFILE_IDS.includes(pid)) return true;
  return isPeruFederalTaxIdForWholesale(ctx?.customerRut);
}

/**
 * Resuelve el porcentaje de descuento del socio SAP para la marca de cabecera (Carga Mayorista).
 * Perú + DOCKERS: requiere `dockersGender` (`men` → `DOCKERS-MEN`, `women` → `DOCKERS-WOMEN`).
 *
 * @param {{ DiscountBrand?: Array<{ marca?: string, porcentaje?: number|string }> }|null|undefined} partner
 * @param {string|null|undefined} brand
 * @param {{ dockersGender?: 'men'|'women'|'', isPeruContext?: boolean }} [options]
 * @returns {number}
 */
export function resolveBulkBrandDiscount(partner, brand, options = {}) {
  if (!partner || !brand) return 0;
  const list = Array.isArray(partner.DiscountBrand) ? partner.DiscountBrand : [];
  if (list.length === 0) return 0;
  const brandNorm = String(brand).trim();
  if (!brandNorm) return 0;

  const isPeru = Boolean(options.isPeruContext);
  const gender = String(options.dockersGender ?? '').trim().toLowerCase();

  if (isDockersBrandName(brandNorm) && isPeru) {
    if (gender !== 'men' && gender !== 'women') return 0;
    const key = gender === 'women' ? 'DOCKERS-WOMEN' : 'DOCKERS-MEN';
    const match = list.find((b) => normalizeDiscountBrandMarcaKey(b?.marca) === key);
    const n = Number(match?.porcentaje);
    return Number.isFinite(n) ? n : 0;
  }

  if (isDockersBrandName(brandNorm) && !isPeru) {
    const match = list.find((b) => normalizeDiscountBrandMarcaKey(b?.marca) === 'DOCKERS');
    const n = Number(match?.porcentaje);
    if (Number.isFinite(n)) return n;
  }

  const target = normalizeDiscountBrandMarcaKey(brandNorm);
  const match = list.find((b) => normalizeDiscountBrandMarcaKey(b?.marca) === target);
  const n = Number(match?.porcentaje);
  return Number.isFinite(n) ? n : 0;
}

/**
 * ¿Puede iniciarse la consulta SAP de ítems en Carga Mayorista?
 * Solo Perú + DOCKERS exige línea (men/women) y descuento de marca > 0; el resto de contextos siempre listo.
 * Con `discountOptional` (IX Comercio Perú, sin descuento de negocio) basta con la línea elegida.
 *
 * @param {{ isPeruContext?: boolean, brand?: string|null, dockersGender?: string|null, brandDiscountPercent?: number|null, discountOptional?: boolean }} ctx
 * @returns {boolean}
 */
export function isBulkPeruDockersSapItemsFetchReady(ctx = {}) {
  if (!ctx.isPeruContext || !isDockersBrandName(ctx.brand)) return true;
  const g = String(ctx.dockersGender ?? '').trim().toLowerCase();
  if (g !== 'men' && g !== 'women') return false;
  if (ctx.discountOptional) return true;
  return Number(ctx.brandDiscountPercent) > 0;
}

/**
 * Normaliza id fiscal para query: RUT Chile o RUN/RUC Perú.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string} cadena lista para `federalTaxId`, o `''` si no es usable
 *
 * @example normalizeFederalTaxIdForQuery('93.834.000-5') // '93834000-5'
 * @example normalizeFederalTaxIdForQuery('81201000-K') // '81201000-K'
 * @example normalizeFederalTaxIdForQuery('20-123456789') // '20123456789' (RUC)
 * @example normalizeFederalTaxIdForQuery('12345678') // '12345678' (DNI/RUN)
 */
export function normalizeFederalTaxIdForQuery(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s || s === '—' || s === '-') return '';

  const noSpaces = s.replace(/\s/g, '');
  const withoutDots = noSpaces.replace(/\./g, '');

  // Chile: cuerpo numérico + guion + DV (dígito o K)
  const chileMatch = withoutDots.match(/^(\d+)-([\dkK])$/);
  if (chileMatch) {
    const body = chileMatch[1];
    const dv = chileMatch[2].toUpperCase();
    if (body.length >= 1) {
      return `${body}-${dv}`;
    }
  }

  // Perú / numérico: RUC 11 dígitos o DNI/RUN 8 dígitos (quitar separadores decorativos)
  const digitsOnly = s.replace(/\D/g, '');
  if (digitsOnly.length === 8 || digitsOnly.length === 11) {
    return digitsOnly;
  }

  return '';
}

/**
 * @param {unknown} payload
 * @returns {SapB1BusinessPartnerDto|null}
 */
function unwrapBusinessPartnerPayload(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const p = /** @type {{ code?: string, data?: unknown }} */ (payload);
  if (p.code === 'SUCCESS' && Array.isArray(p.data) && p.data.length > 0) {
    const first = p.data[0];
    return first && typeof first === 'object' ? /** @type {SapB1BusinessPartnerDto} */ (first) : null;
  }
  return null;
}

/**
 * Busca un socio en SAP B1 por id fiscal (Chile / Perú).
 *
 * @param {string|number|null|undefined} federalTaxId - RUT, RUC o RUN tal como venga de la OV
 * @returns {Promise<SapB1BusinessPartnerDto|null>} primer resultado o `null` si no hay match / payload vacío
 */
export async function fetchSapB1BusinessPartnerByFederalTaxId(federalTaxId) {
  const normalized = normalizeFederalTaxIdForQuery(federalTaxId);
  if (!normalized) {
    return null;
  }

  const response = await axiosInstance.get('integration/sapb1/business-partners', {
    params: { federalTaxId: normalized }
  });

  return unwrapBusinessPartnerPayload(response.data);
}

/**
 * Arma una línea de dirección legible desde el DTO (facturación / fiscal).
 *
 * @param {SapB1BusinessPartnerDto|null|undefined} p
 * @returns {string}
 */
export function formatSapB1BillingAddressLine(p) {
  if (!p || typeof p !== 'object') return '';
  const parts = [p.Address, p.City, p.County].filter((x) => x != null && String(x).trim() !== '');
  if (parts.length) return parts.join(', ');
  const mail = [p.MailAddress, p.MailCity, p.MailCounty].filter((x) => x != null && String(x).trim() !== '');
  return mail.length ? mail.join(', ') : '';
}
